import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, Paper, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useApiGet } from '../../utils/httpClientTs';
import {
  createElfCheckBatch,
  getElfCheckBatches,
  getElfPendingChecksBySource,
} from '../../endpoints/financeElfPayments';
import ServerSideTable, {
  ServerSideTableColumn,
} from '../../Components/ServerSideTable';
import type { CheckBatchSummary } from '../../types/financePayments';
import CreateCheckBatchDialog from './CreateCheckBatchDialog';

const fmtMoney = (n: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const CHECK_BATCH_LIST_QUERY_OPTS = {
  enabled: true,
  retry: 1,
  refetchOnMount: 'always' as const,
};

const ElfPaymentsCheckBatchesPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const { data, isLoading } = useApiGet(
    ['finance-elf-payments-batches', page, rowsPerPage],
    () =>
      getElfCheckBatches({
        page: page + 1,
        page_size: rowsPerPage,
      }),
    CHECK_BATCH_LIST_QUERY_OPTS
  );

  const { data: pendingBySource } = useApiGet(
    ['finance-elf-payments-pending-by-source'],
    getElfPendingChecksBySource,
    CHECK_BATCH_LIST_QUERY_OPTS
  );

  const rows = data?.data ?? [];
  const pagination = data?.pagination;
  const elfPending =
    pendingBySource?.items.find((i) => i.source === 'elf_loans')?.count ?? 0;

  const columns: ServerSideTableColumn<CheckBatchSummary>[] = [
    {
      id: 'batch_number',
      label: 'Batch',
      width: '22%',
      firstCell: true,
      sortable: false,
      render: (_v, row) => row.batch_number,
    },
    {
      id: 'status',
      label: 'Status',
      width: '12%',
      sortable: false,
      render: (_v, row) => row.status,
    },
    {
      id: 'check_count',
      label: 'Checks',
      width: '10%',
      sortable: false,
      render: (_v, row) => String(row.check_count),
    },
    {
      id: 'total_amount',
      label: 'Total',
      width: '12%',
      sortable: false,
      render: (_v, row) => fmtMoney(row.total_amount),
    },
    {
      id: 'created_at',
      label: 'Created',
      width: '18%',
      sortable: false,
      render: (_v, row) =>
        row.created_at ? new Date(row.created_at).toLocaleString() : '—',
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          ELF loan check batches only. Open a batch, add pending ELF checks,
          finalize when ready to deposit, and print the slip as often as needed.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          New batch
        </Button>
      </Box>

      {elfPending > 0 ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Unbatched pending ELF checks
          </Typography>
          <Chip
            size="small"
            color="primary"
            label={`Total pending: ${elfPending}`}
          />
        </Paper>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No unbatched pending ELF checks in the queue.
        </Typography>
      )}

      <ServerSideTable<CheckBatchSummary>
        data={rows}
        columns={columns}
        loading={isLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={pagination?.total_count ?? 0}
        totalPages={pagination?.total_pages ?? 0}
        hasNext={pagination?.has_next ?? false}
        hasPrev={pagination?.has_prev ?? false}
        order="desc"
        orderBy="created_at"
        onPageChange={setPage}
        onRowsPerPageChange={(n) => {
          setRowsPerPage(n);
          setPage(0);
        }}
        onSort={() => {}}
        onRowClick={(row) =>
          navigate(`/finance/elf-payments/check-batches/${row.id}`)
        }
        emptyMessage="No ELF batches yet. Create one to start deposit prep."
      />

      <CreateCheckBatchDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        createBatch={createElfCheckBatch}
        listQueryKeys={[
          ['finance-elf-payments-batches'],
          ['finance-elf-payments-pending-by-source'],
        ]}
        onCreated={(res) => {
          if (res?.batch?.id) {
            navigate(`/finance/elf-payments/check-batches/${res.batch.id}`);
          }
        }}
      />
    </Box>
  );
};

export default ElfPaymentsCheckBatchesPage;
